import {
  collapseFrameDuration,
  collapseFrameTranslation,
  collapseTranslations,
  localesWithContent,
  localizeElement,
  localizeFrame,
} from 'helpers/localize';
import { DEFAULT_STORY_DEFAULTS } from 'context/StoryDefaultsProvider';
import {
  BASE_STYLE,
  TElementStyle,
  TElementType,
  TStoryElement,
} from 'context/StudioEditorProvider';
import { resolveStoryDuration } from 'constants/storyDuration';
import { DEFAULT_STORY_LOCALE, TStoryLocale } from 'constants/storyLocales';
import { TStoryItem, TStoryVersionedFrame } from 'types/story';
import {
  TStoryCoverWire,
  TStoryElementStyleWire,
  TStoryElementWire,
  TStoryItemWire,
  TStoryLocalizedItemsWire,
} from 'types/storyWire';

/**
 * The wire boundary, both directions. It lives in its own module because the save path
 * (`mapping.ts`) and the content comparison (`divergence.ts`) both need it, and putting it in
 * either would make them import each other.
 *
 * The projection is a whitelist, not a spread: each element type emits exactly the fields its
 * variant in `types/storyWire.ts` declares. That is what makes the compiler — rather than a
 * reviewer — responsible for keeping the payload and the contract in step.
 */

/**
 * The contract requires every style field except `backgroundColor`, but `applyStyleDefaults`
 * strips the ones a given type cannot use when the element is created. Backfill those from
 * `BASE_STYLE` — a field the author never set is one they have no opinion about, so the default
 * is the honest value to send. A field they DID set is carried through untouched, which is what
 * keeps the read path (`fromWireElement`) able to be the identity.
 */
const toWireStyle = (style: TElementStyle, type: TElementType): TStoryElementStyleWire => {
  const wire: TStoryElementStyleWire = {
    color: style.color,
    backgroundColor: style.backgroundColor,
    fontSize: style.fontSize ?? BASE_STYLE.fontSize,
    fontFamily: style.fontFamily ?? BASE_STYLE.fontFamily,
    fontWeight: style.fontWeight ?? BASE_STYLE.fontWeight,
    textAlign: style.textAlign ?? BASE_STYLE.textAlign,
    verticalAlign: style.verticalAlign ?? BASE_STYLE.verticalAlign,
    lineHeight: style.lineHeight ?? BASE_STYLE.lineHeight,
    borderRadius: style.borderRadius ?? BASE_STYLE.borderRadius,
    opacity: style.opacity,
  };

  // Text draws on the frame background; a fill would box the glyphs.
  if (type === 'text') {
    delete wire.backgroundColor;
  }
  return wire;
};

const toWireElement = (folded: TStoryElement): TStoryElementWire => {
  /*
   * Interactive elements carry no element style — their whole appearance is `widgetVariant`, and
   * nothing renders or edits a style on them — so the shared geometry is the base and `style` is
   * added only for the types that have one. `emoji` is not a widget and keeps its style.
   */
  const widgetBase = {
    id: folded.id,
    x: folded.x,
    y: folded.y,
    width: folded.width,
    height: folded.height,
    rotation: folded.rotation ?? 0,
    zIndex: folded.zIndex,
    animation: folded.animation,
    eventName: folded.eventName,
  };
  const base = {
    ...widgetBase,
    // The fallback is unreachable for these types: only widgets arrive without a style.
    style: toWireStyle(folded.style ?? BASE_STYLE, folded.type),
  };

  switch (folded.type) {
    case 'text':
      return { ...base, type: 'text', content: folded.content ?? '' };

    case 'button':
    case 'link':
    case 'swipe-up':
      return {
        ...base,
        type: folded.type,
        content: folded.content ?? '',
        // `validateFrames` blocks a save without both, so the fallbacks are unreachable in practice.
        ctaMobile: folded.ctaMobile ?? '',
        ctaWeb: folded.ctaWeb ?? '',
        fitWidth: folded.fitWidth,
      };

    case 'shape':
      return { ...base, type: 'shape' };

    case 'image':
    case 'video':
      return { ...base, type: folded.type, src: folded.src ?? '', autoplay: folded.autoplay };

    case 'emoji':
      return { ...base, type: 'emoji', content: folded.content };

    case 'poll':
      return {
        ...widgetBase,
        type: 'poll',
        widgetSize: folded.widgetSize,
        widgetVariant: folded.widgetVariant,
        content: folded.content,
        question: folded.question ?? '',
        options: folded.options ?? [],
        results: folded.results,
        pollLayout: folded.pollLayout,
        correctIndex: folded.correctIndex,
      };

    // A quiz has no `question`: its prompt travels in `content`, like every other widget label.
    case 'quiz':
      return {
        ...widgetBase,
        type: 'quiz',
        widgetSize: folded.widgetSize,
        widgetVariant: folded.widgetVariant,
        content: folded.content,
        options: folded.options ?? [],
        correctIndex: folded.correctIndex ?? 0,
        reveal: folded.reveal,
      };

    case 'emoji-slider':
      return {
        ...widgetBase,
        type: 'emoji-slider',
        widgetSize: folded.widgetSize,
        widgetVariant: folded.widgetVariant,
        content: folded.content ?? '',
        emoji: folded.emoji ?? '',
      };

    case 'reaction':
      return {
        ...widgetBase,
        type: 'reaction',
        widgetSize: folded.widgetSize,
        widgetVariant: folded.widgetVariant,
        content: folded.content,
        emojis: folded.emojis ?? [],
      };

    case 'countdown':
      return {
        ...widgetBase,
        type: 'countdown',
        widgetSize: folded.widgetSize,
        widgetVariant: folded.widgetVariant,
        content: folded.content,
        targetAt: folded.targetAt ?? '',
        countdownStyle: folded.countdownStyle,
        endedText: folded.endedText,
      };

    // Unreachable: `never` makes the compiler flag any element type added without a projection.
    default: {
      const unhandled: never = folded.type;
      throw new Error(`No wire projection for element type: ${String(unhandled)}`);
    }
  }
};

/**
 * The canonical wire form of the slides: one complete bucket per locale.
 *
 * The projection belongs here rather than at the save call sites because `itemContent` runs this on
 * both sides of a comparison — so whatever normalisation happens on save has to happen to the live
 * snapshot too, or identical content reads as changed.
 *
 * Copy and the backdrop differ between buckets. `localizeElement` resolves each element and
 * `localizeFrame` the slide's `background`/`cover` (both with base fallback), so every bucket is
 * complete on its own and the device never has to merge two languages.
 */
export const toWireItems = (frames: TStoryVersionedFrame[]): TStoryLocalizedItemsWire => {
  const itemsFor = (locale: TStoryLocale): TStoryItemWire[] =>
    frames.map((frame) => {
      const localized = localizeFrame(frame, locale);
      return {
        id: frame.id,
        name: frame.name,
        background: localized.background,
        statusBar: frame.statusBar,
        durationSeconds: resolveStoryDuration(localized.durationSeconds),
        // Required by the contract: an empty object is "solid background, no cover media".
        cover: localized.cover ?? {},
        elements: frame.elements.map((element: TStoryElement) =>
          toWireElement(localizeElement(element, locale))
        ),
        // Shared across buckets: one slide cannot be at two revisions at once.
        v: frame.v ?? 1,
      };
    });

  const buckets: TStoryLocalizedItemsWire = { en: itemsFor(DEFAULT_STORY_LOCALE) };
  localesWithContent(frames)
    .filter((locale) => locale !== DEFAULT_STORY_LOCALE)
    .forEach((locale) => {
      buckets[locale] = itemsFor(locale);
    });
  return buckets;
};

/**
 * Style is carried through untouched. Trimming the fields a type "has no use for" here would be
 * wrong twice over: media `borderRadius` does render, and any trim makes the round-trip lossy —
 * a load-then-save with no edits would rewrite the slide and bump every `v`.
 *
 * The one synthesis: interactive elements ship no style at all, but `TStoryElement` requires one
 * and both canvases read `style.opacity` off the box. The two-field minimum below is never
 * persisted — `toWireElement` drops it again — so the round-trip stays absent → minimum → absent.
 */
const fromWireElement = (element: TStoryElementWire, index: number): TStoryElement => {
  const loose = element as TStoryElementWire & { style?: TElementStyle };
  return {
    ...(element as unknown as TStoryElement),
    rotation: element.rotation ?? 0,
    zIndex: element.zIndex ?? index,
    style: loose.style ?? { color: BASE_STYLE.color, opacity: BASE_STYLE.opacity },
  };
};

/**
 * The backdrop half of an item, normalised. Its own function because the read path needs it twice:
 * once for the canonical `en` frame, and once per translation bucket to fold back into
 * `frame.translations`.
 */
const wireBackdrop = (item: TStoryItemWire): { background: string; cover?: TStoryCoverWire } => ({
  background: item.background ?? DEFAULT_STORY_DEFAULTS.frameBackground,
  // `{}` back to absent, so the round-trip through `toWireItems` is the identity.
  cover: item.cover && Object.keys(item.cover).length > 0 ? item.cover : undefined,
});

const fromWireItem = (item: TStoryItemWire): TStoryItem => ({
  id: item.id,
  v: item.v ?? 1,
  name: item.name,
  ...wireBackdrop(item),
  statusBar: item.statusBar,
  durationSeconds: resolveStoryDuration(item.durationSeconds),
  elements: item.elements.map(fromWireElement),
});

/**
 * The inverse of the wire form: `en` is the canonical story, and every other bucket is folded back
 * into the `translations` it came from — per element for copy, per frame for the backdrop.
 *
 * Matching is by id, which the mirrored-structure guarantee makes safe. An id that appears in a
 * translation but not in `en` is skipped rather than invented — `en` defines what exists.
 */
export const fromWireItems = (itemsByLocale: TStoryLocalizedItemsWire): TStoryItem[] => {
  /*
   * Defensive on the way in, because this is the one function the whole groups list runs on every
   * group: `items` arrives from an untyped JsonNode and has already shipped as `{}` for an untouched
   * draft, and as a flat array before it was grouped by locale. A throw here empties the list with
   * no console error (React Query swallows it as error state), which is exactly how it broke before.
   */
  const buckets: TStoryLocalizedItemsWire = Array.isArray(itemsByLocale)
    ? { en: itemsByLocale }
    : (itemsByLocale ?? { en: [] });
  const canonical = (buckets.en ?? []).map(fromWireItem);
  const byId = new Map(canonical.map((item) => [item.id, item]));

  (Object.keys(buckets) as TStoryLocale[])
    .filter((locale) => locale !== DEFAULT_STORY_LOCALE)
    .forEach((locale) => {
      (buckets[locale] ?? []).forEach((wireItem) => {
        const target = byId.get(wireItem.id);
        if (!target) {
          return;
        }
        const translated = new Map(
          wireItem.elements.map((el, i) => [el.id, fromWireElement(el, i)])
        );
        target.elements = target.elements.map((element) => {
          const other = translated.get(element.id);
          return other ? collapseTranslations(element, other, locale) : element;
        });
        // Assigned back onto `target` rather than replacing it: `byId` hands out references into
        // `canonical`, which is what the function returns.
        target.translations = collapseFrameTranslation(
          target,
          { ...target, ...wireBackdrop(wireItem) },
          locale
        ).translations;
        target.durations = collapseFrameDuration(
          target,
          wireItem.durationSeconds,
          locale
        ).durations;
      });
    });

  return canonical;
};
